import * as z from 'zod';

export const ItemUploadCallbackRequest = z.object({
  options: z
    .object({
      itemId: z
        .string()
        .describe('ID of the created item.')
        .max(100)
        .optional(),
      callbackToken: z
        .string()
        .describe('Callback token provided to the upload endpoint.')
        .max(10000)
        .optional(),
    })
    .optional(),
});
export const ItemUploadCallbackResponse = z.object({
  message: z
    .string()
    .describe('Message describing the callback result.')
    .optional()
    .nullable(),
});
export const GenerateFileUploadUrlRequest = z.object({
  options: z
    .object({
      fileName: z
        .string()
        .describe(
          'Original file name including the extension. Used as the initial display name for the created item.'
        )
        .max(1000)
        .optional(),
      contentType: z
        .string()
        .describe('MIME type of the file. Used to identify the media type.')
        .max(1000)
        .optional(),
      sizeInBytes: z.number().int().describe('File size in bytes.').optional(),
      categoryIds: z.array(z.string()).max(20).optional(),
    })
    .optional(),
});
export const GenerateFileUploadUrlResponse = z.object({
  uploadUrl: z
    .string()
    .describe('Upload URL where the file should be uploaded.')
    .max(10000)
    .optional(),
});
export const ImportFileRequest = z.object({
  url: z.string().describe('URL of the file to import.').url(),
  options: z
    .object({
      fileName: z
        .string()
        .describe(
          'Original file name including the extension. Used as the initial display name for the created item.'
        )
        .max(1000)
        .optional()
        .nullable(),
      contentType: z
        .string()
        .describe('MIME type of the file. Used to identify the media type.')
        .max(1000)
        .optional()
        .nullable(),
      sizeInBytes: z
        .number()
        .int()
        .describe('File size in bytes.')
        .optional()
        .nullable(),
      categoryIds: z.array(z.string()).max(20).optional(),
      mediaType: z
        .enum([
          'MIXED',
          'IMAGE',
          'VIDEO',
          'AUDIO',
          'DOCUMENT',
          'VECTOR',
          'ARCHIVE',
          'MODEL3D',
        ])
        .optional(),
      uploaderSystemTag: z
        .string()
        .describe(
          'Identifier of the external app or system that imported the file. Used to filter items by uploader. For example, if multiple apps upload to the same account, each app can use its own identifier to retrieve only items it created. Stored in `systemTags` prefixed by `_external_uploader:`.'
        )
        .max(100)
        .optional()
        .nullable(),
      uploaderInfoSystemTag: z
        .string()
        .describe(
          'Additional metadata about the external source. Use this to store identifiers of related entities in external systems, such as external item IDs, source URLs, or reference numbers. This complements `uploaderSystemTag`, which identifies the importing app itself. Stored in `systemTags` prefixed by `_external_uploader_info:`.'
        )
        .max(1000)
        .optional()
        .nullable(),
    })
    .optional(),
});
export const ImportFileResponse = z.object({
  item: z
    .object({
      _id: z.string().describe('Item ID.').max(100).optional(),
      mediaType: z
        .enum([
          'MIXED',
          'IMAGE',
          'VIDEO',
          'AUDIO',
          'DOCUMENT',
          'VECTOR',
          'ARCHIVE',
          'MODEL3D',
        ])
        .describe('Media type of the item.')
        .optional(),
      sizeInBytes: z
        .string()
        .describe('Size of the uploaded file in bytes.')
        .max(1000)
        .optional()
        .nullable(),
      title: z
        .string()
        .describe(
          'Title of the item. Used for searching and displaying the item.'
        )
        .max(1000)
        .optional()
        .nullable(),
      assets: z
        .intersection(
          z.object({}),
          z.xor([
            z.object({
              image: z.never().optional(),
              video: z.never().optional(),
              vector: z.never().optional(),
              audio: z.never().optional(),
              document: z.never().optional(),
              archive: z.never().optional(),
              model3d: z.never().optional(),
            }),
            z.object({
              video: z.never().optional(),
              vector: z.never().optional(),
              audio: z.never().optional(),
              document: z.never().optional(),
              archive: z.never().optional(),
              model3d: z.never().optional(),
              image: z.string().describe('Image assets and metadata.'),
            }),
            z.object({
              image: z.never().optional(),
              vector: z.never().optional(),
              audio: z.never().optional(),
              document: z.never().optional(),
              archive: z.never().optional(),
              model3d: z.never().optional(),
              video: z.string().describe('Video assets and metadata.'),
            }),
            z.object({
              image: z.never().optional(),
              video: z.never().optional(),
              audio: z.never().optional(),
              document: z.never().optional(),
              archive: z.never().optional(),
              model3d: z.never().optional(),
              vector: z.string().describe('Vector image assets and metadata.'),
            }),
            z.object({
              image: z.never().optional(),
              video: z.never().optional(),
              vector: z.never().optional(),
              document: z.never().optional(),
              archive: z.never().optional(),
              model3d: z.never().optional(),
              audio: z.string().describe('Audio assets and metadata.'),
            }),
            z.object({
              image: z.never().optional(),
              video: z.never().optional(),
              vector: z.never().optional(),
              audio: z.never().optional(),
              archive: z.never().optional(),
              model3d: z.never().optional(),
              document: z.string().describe('Document assets and metadata.'),
            }),
            z.object({
              image: z.never().optional(),
              video: z.never().optional(),
              vector: z.never().optional(),
              audio: z.never().optional(),
              document: z.never().optional(),
              model3d: z.never().optional(),
              archive: z
                .object({
                  _id: z.string().describe('WixMedia ID.').optional(),
                  url: z.string().describe('Archive URL.').optional(),
                  urlExpirationDate: z
                    .date()
                    .describe('Archive URL expiration date (when relevant).')
                    .optional()
                    .nullable(),
                  sizeInBytes: z
                    .string()
                    .describe('Archive size in bytes.')
                    .optional()
                    .nullable(),
                  filename: z
                    .string()
                    .describe('Archive filename.')
                    .optional()
                    .nullable(),
                })
                .describe('Archive file metadata.'),
            }),
            z.object({
              image: z.never().optional(),
              video: z.never().optional(),
              vector: z.never().optional(),
              audio: z.never().optional(),
              document: z.never().optional(),
              archive: z.never().optional(),
              model3d: z
                .object({
                  _id: z.string().describe('WixMedia 3D ID.').optional(),
                  url: z.string().describe('3D URL.').optional(),
                  thumbnail: z
                    .string()
                    .describe('3D thumbnail Image')
                    .optional(),
                  altText: z
                    .string()
                    .describe('3D alt text.')
                    .optional()
                    .nullable(),
                  urlExpirationDate: z
                    .date()
                    .describe('3D URL expiration date (when relevant).')
                    .optional()
                    .nullable(),
                  filename: z
                    .string()
                    .describe('3D filename.')
                    .optional()
                    .nullable(),
                  sizeInBytes: z
                    .string()
                    .describe('3D size in bytes.')
                    .optional()
                    .nullable(),
                })
                .describe('3D model metadata.'),
            }),
          ])
        )
        .describe(
          'Item assets including URLs for different renditions and formats.'
        )
        .optional(),
      displayTags: z.array(z.string()).max(100).optional(),
      internalTags: z.array(z.string()).max(100).optional(),
      systemTags: z.array(z.string()).max(100).optional(),
      parentCategoryIds: z.array(z.string()).max(100).optional(),
      publishStatus: z
        .enum(['UNDEFINED', 'UNPUBLISHED', 'PUBLISHED'])
        .describe(
          "Publish status of the item. When `PUBLISHED`, the item is visible in child accounts' media managers. When `UNPUBLISHED`, the item is only visible in the account dashboard."
        )
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the item was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the item was updated.')
        .optional()
        .nullable(),
      internalId: z
        .string()
        .describe(
          'Internal ID used to reference the item within Wix media systems.'
        )
        .max(100)
        .optional()
        .nullable(),
    })
    .describe(
      'Partial item information. At this stage, only `internalId` is populated. All other required fields contain placeholder values.'
    )
    .optional(),
});
export const BulkImportFilesRequest = z.object({
  importFileRequests: z
    .array(
      z.object({
        url: z.string().describe('URL of the file to import.').url(),
        fileName: z
          .string()
          .describe(
            'Original file name including the extension. Used as the initial display name for the created item.'
          )
          .max(1000)
          .optional()
          .nullable(),
        contentType: z
          .string()
          .describe('MIME type of the file. Used to identify the media type.')
          .max(1000)
          .optional()
          .nullable(),
        sizeInBytes: z
          .number()
          .int()
          .describe('File size in bytes.')
          .optional()
          .nullable(),
        categoryIds: z.array(z.string()).max(20).optional(),
        mediaType: z
          .enum([
            'MIXED',
            'IMAGE',
            'VIDEO',
            'AUDIO',
            'DOCUMENT',
            'VECTOR',
            'ARCHIVE',
            'MODEL3D',
          ])
          .optional(),
        uploaderSystemTag: z
          .string()
          .describe(
            'Identifier of the external app or system that imported the file. Used to filter items by uploader. For example, if multiple apps upload to the same account, each app can use its own identifier to retrieve only items it created. Stored in `systemTags` prefixed by `_external_uploader:`.'
          )
          .max(100)
          .optional()
          .nullable(),
        uploaderInfoSystemTag: z
          .string()
          .describe(
            'Additional metadata about the external source. Use this to store identifiers of related entities in external systems, such as external item IDs, source URLs, or reference numbers. This complements `uploaderSystemTag`, which identifies the importing app itself. Stored in `systemTags` prefixed by `_external_uploader_info:`.'
          )
          .max(1000)
          .optional()
          .nullable(),
      })
    )
    .min(1)
    .max(100),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe(
          'Whether to include the imported item in the response. Set to `false` to exclude the item from the returned object.\n\nDefault: `true`'
        )
        .optional()
        .nullable(),
      categoryIds: z.array(z.string()).max(20).optional(),
    })
    .optional(),
});
export const BulkImportFilesResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata about the import operation result.')
          .optional(),
        item: z
          .object({
            _id: z.string().describe('Item ID.').max(100).optional(),
            mediaType: z
              .enum([
                'MIXED',
                'IMAGE',
                'VIDEO',
                'AUDIO',
                'DOCUMENT',
                'VECTOR',
                'ARCHIVE',
                'MODEL3D',
              ])
              .describe('Media type of the item.')
              .optional(),
            sizeInBytes: z
              .string()
              .describe('Size of the uploaded file in bytes.')
              .max(1000)
              .optional()
              .nullable(),
            title: z
              .string()
              .describe(
                'Title of the item. Used for searching and displaying the item.'
              )
              .max(1000)
              .optional()
              .nullable(),
            assets: z
              .intersection(
                z.object({}),
                z.xor([
                  z.object({
                    image: z.never().optional(),
                    video: z.never().optional(),
                    vector: z.never().optional(),
                    audio: z.never().optional(),
                    document: z.never().optional(),
                    archive: z.never().optional(),
                    model3d: z.never().optional(),
                  }),
                  z.object({
                    video: z.never().optional(),
                    vector: z.never().optional(),
                    audio: z.never().optional(),
                    document: z.never().optional(),
                    archive: z.never().optional(),
                    model3d: z.never().optional(),
                    image: z.string().describe('Image assets and metadata.'),
                  }),
                  z.object({
                    image: z.never().optional(),
                    vector: z.never().optional(),
                    audio: z.never().optional(),
                    document: z.never().optional(),
                    archive: z.never().optional(),
                    model3d: z.never().optional(),
                    video: z.string().describe('Video assets and metadata.'),
                  }),
                  z.object({
                    image: z.never().optional(),
                    video: z.never().optional(),
                    audio: z.never().optional(),
                    document: z.never().optional(),
                    archive: z.never().optional(),
                    model3d: z.never().optional(),
                    vector: z
                      .string()
                      .describe('Vector image assets and metadata.'),
                  }),
                  z.object({
                    image: z.never().optional(),
                    video: z.never().optional(),
                    vector: z.never().optional(),
                    document: z.never().optional(),
                    archive: z.never().optional(),
                    model3d: z.never().optional(),
                    audio: z.string().describe('Audio assets and metadata.'),
                  }),
                  z.object({
                    image: z.never().optional(),
                    video: z.never().optional(),
                    vector: z.never().optional(),
                    audio: z.never().optional(),
                    archive: z.never().optional(),
                    model3d: z.never().optional(),
                    document: z
                      .string()
                      .describe('Document assets and metadata.'),
                  }),
                  z.object({
                    image: z.never().optional(),
                    video: z.never().optional(),
                    vector: z.never().optional(),
                    audio: z.never().optional(),
                    document: z.never().optional(),
                    model3d: z.never().optional(),
                    archive: z
                      .object({
                        _id: z.string().describe('WixMedia ID.').optional(),
                        url: z.string().describe('Archive URL.').optional(),
                        urlExpirationDate: z
                          .date()
                          .describe(
                            'Archive URL expiration date (when relevant).'
                          )
                          .optional()
                          .nullable(),
                        sizeInBytes: z
                          .string()
                          .describe('Archive size in bytes.')
                          .optional()
                          .nullable(),
                        filename: z
                          .string()
                          .describe('Archive filename.')
                          .optional()
                          .nullable(),
                      })
                      .describe('Archive file metadata.'),
                  }),
                  z.object({
                    image: z.never().optional(),
                    video: z.never().optional(),
                    vector: z.never().optional(),
                    audio: z.never().optional(),
                    document: z.never().optional(),
                    archive: z.never().optional(),
                    model3d: z
                      .object({
                        _id: z.string().describe('WixMedia 3D ID.').optional(),
                        url: z.string().describe('3D URL.').optional(),
                        thumbnail: z
                          .string()
                          .describe('3D thumbnail Image')
                          .optional(),
                        altText: z
                          .string()
                          .describe('3D alt text.')
                          .optional()
                          .nullable(),
                        urlExpirationDate: z
                          .date()
                          .describe('3D URL expiration date (when relevant).')
                          .optional()
                          .nullable(),
                        filename: z
                          .string()
                          .describe('3D filename.')
                          .optional()
                          .nullable(),
                        sizeInBytes: z
                          .string()
                          .describe('3D size in bytes.')
                          .optional()
                          .nullable(),
                      })
                      .describe('3D model metadata.'),
                  }),
                ])
              )
              .describe(
                'Item assets including URLs for different renditions and formats.'
              )
              .optional(),
            displayTags: z.array(z.string()).max(100).optional(),
            internalTags: z.array(z.string()).max(100).optional(),
            systemTags: z.array(z.string()).max(100).optional(),
            parentCategoryIds: z.array(z.string()).max(100).optional(),
            publishStatus: z
              .enum(['UNDEFINED', 'UNPUBLISHED', 'PUBLISHED'])
              .describe(
                "Publish status of the item. When `PUBLISHED`, the item is visible in child accounts' media managers. When `UNPUBLISHED`, the item is only visible in the account dashboard."
              )
              .optional(),
            _createdDate: z
              .date()
              .describe('Date and time the item was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the item was updated.')
              .optional()
              .nullable(),
            internalId: z
              .string()
              .describe(
                'Internal ID used to reference the item within Wix media systems.'
              )
              .max(100)
              .optional()
              .nullable(),
          })
          .describe(
            'Imported item. Returned only if the operation was successful and `returnEntity` is not set to `false`.'
          )
          .optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Metadata about the bulk operation.')
    .optional(),
});
export const SearchItemsRequest = z.object({
  query: z
    .object({
      filter: z
        .object({
          parentCategoryIds: z
            .object({ $hasSome: z.array(z.string()) })
            .partial()
            .strict()
            .optional(),
          title: z
            .union([
              z.string(),
              z.object({ $eq: z.string() }).partial().strict(),
            ])
            .optional(),
          _createdDate: z
            .union([
              z.string(),
              z.object({ $eq: z.string() }).partial().strict(),
            ])
            .optional(),
          _updatedDate: z
            .union([
              z.string(),
              z.object({ $eq: z.string() }).partial().strict(),
            ])
            .optional(),
          sizeInBytes: z
            .union([
              z.string(),
              z.object({ $eq: z.string() }).partial().strict(),
            ])
            .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z
        .array(
          z.object({
            fieldName: z
              .enum(['title', '_createdDate', '_updatedDate', 'sizeInBytes'])
              .optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .catchall(z.any())
    .describe(
      'Search query for text-based matching across item `title`, `displayTags`, and `internalTags` fields. Partial matching is supported for `title` and `displayTags`. `internalTags` require a full exact match. All filters support equality only. Each query must include a `categoryId` filter.'
    ),
});
export const SearchItemsResponse = z.object({
  items: z
    .array(
      z.object({
        _id: z.string().describe('Item ID.').max(100).optional(),
        mediaType: z
          .enum([
            'MIXED',
            'IMAGE',
            'VIDEO',
            'AUDIO',
            'DOCUMENT',
            'VECTOR',
            'ARCHIVE',
            'MODEL3D',
          ])
          .describe('Media type of the item.')
          .optional(),
        sizeInBytes: z
          .string()
          .describe('Size of the uploaded file in bytes.')
          .max(1000)
          .optional()
          .nullable(),
        title: z
          .string()
          .describe(
            'Title of the item. Used for searching and displaying the item.'
          )
          .max(1000)
          .optional()
          .nullable(),
        assets: z
          .intersection(
            z.object({}),
            z.xor([
              z.object({
                image: z.never().optional(),
                video: z.never().optional(),
                vector: z.never().optional(),
                audio: z.never().optional(),
                document: z.never().optional(),
                archive: z.never().optional(),
                model3d: z.never().optional(),
              }),
              z.object({
                video: z.never().optional(),
                vector: z.never().optional(),
                audio: z.never().optional(),
                document: z.never().optional(),
                archive: z.never().optional(),
                model3d: z.never().optional(),
                image: z.string().describe('Image assets and metadata.'),
              }),
              z.object({
                image: z.never().optional(),
                vector: z.never().optional(),
                audio: z.never().optional(),
                document: z.never().optional(),
                archive: z.never().optional(),
                model3d: z.never().optional(),
                video: z.string().describe('Video assets and metadata.'),
              }),
              z.object({
                image: z.never().optional(),
                video: z.never().optional(),
                audio: z.never().optional(),
                document: z.never().optional(),
                archive: z.never().optional(),
                model3d: z.never().optional(),
                vector: z
                  .string()
                  .describe('Vector image assets and metadata.'),
              }),
              z.object({
                image: z.never().optional(),
                video: z.never().optional(),
                vector: z.never().optional(),
                document: z.never().optional(),
                archive: z.never().optional(),
                model3d: z.never().optional(),
                audio: z.string().describe('Audio assets and metadata.'),
              }),
              z.object({
                image: z.never().optional(),
                video: z.never().optional(),
                vector: z.never().optional(),
                audio: z.never().optional(),
                archive: z.never().optional(),
                model3d: z.never().optional(),
                document: z.string().describe('Document assets and metadata.'),
              }),
              z.object({
                image: z.never().optional(),
                video: z.never().optional(),
                vector: z.never().optional(),
                audio: z.never().optional(),
                document: z.never().optional(),
                model3d: z.never().optional(),
                archive: z
                  .object({
                    _id: z.string().describe('WixMedia ID.').optional(),
                    url: z.string().describe('Archive URL.').optional(),
                    urlExpirationDate: z
                      .date()
                      .describe('Archive URL expiration date (when relevant).')
                      .optional()
                      .nullable(),
                    sizeInBytes: z
                      .string()
                      .describe('Archive size in bytes.')
                      .optional()
                      .nullable(),
                    filename: z
                      .string()
                      .describe('Archive filename.')
                      .optional()
                      .nullable(),
                  })
                  .describe('Archive file metadata.'),
              }),
              z.object({
                image: z.never().optional(),
                video: z.never().optional(),
                vector: z.never().optional(),
                audio: z.never().optional(),
                document: z.never().optional(),
                archive: z.never().optional(),
                model3d: z
                  .object({
                    _id: z.string().describe('WixMedia 3D ID.').optional(),
                    url: z.string().describe('3D URL.').optional(),
                    thumbnail: z
                      .string()
                      .describe('3D thumbnail Image')
                      .optional(),
                    altText: z
                      .string()
                      .describe('3D alt text.')
                      .optional()
                      .nullable(),
                    urlExpirationDate: z
                      .date()
                      .describe('3D URL expiration date (when relevant).')
                      .optional()
                      .nullable(),
                    filename: z
                      .string()
                      .describe('3D filename.')
                      .optional()
                      .nullable(),
                    sizeInBytes: z
                      .string()
                      .describe('3D size in bytes.')
                      .optional()
                      .nullable(),
                  })
                  .describe('3D model metadata.'),
              }),
            ])
          )
          .describe(
            'Item assets including URLs for different renditions and formats.'
          )
          .optional(),
        displayTags: z.array(z.string()).max(100).optional(),
        internalTags: z.array(z.string()).max(100).optional(),
        systemTags: z.array(z.string()).max(100).optional(),
        parentCategoryIds: z.array(z.string()).max(100).optional(),
        publishStatus: z
          .enum(['UNDEFINED', 'UNPUBLISHED', 'PUBLISHED'])
          .describe(
            "Publish status of the item. When `PUBLISHED`, the item is visible in child accounts' media managers. When `UNPUBLISHED`, the item is only visible in the account dashboard."
          )
          .optional(),
        _createdDate: z
          .date()
          .describe('Date and time the item was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the item was updated.')
          .optional()
          .nullable(),
        internalId: z
          .string()
          .describe(
            'Internal ID used to reference the item within Wix media systems.'
          )
          .max(100)
          .optional()
          .nullable(),
      })
    )
    .max(200)
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe(
          'Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe('Cursor pointing to next page in the list of results.')
            .optional()
            .nullable(),
        })
        .describe(
          'Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used.'
        )
        .optional(),
    })
    .describe('Paging information for the search results.')
    .optional(),
});
export const QueryItemsRequest = z.object({
  query: z
    .object({
      filter: z
        .object({
          parentCategoryIds: z
            .object({ $hasSome: z.array(z.string()) })
            .partial()
            .strict()
            .optional(),
          title: z
            .union([
              z.string(),
              z.object({ $eq: z.string() }).partial().strict(),
            ])
            .optional(),
          _createdDate: z
            .union([
              z.string(),
              z.object({ $eq: z.string() }).partial().strict(),
            ])
            .optional(),
          _updatedDate: z
            .union([
              z.string(),
              z.object({ $eq: z.string() }).partial().strict(),
            ])
            .optional(),
          sizeInBytes: z
            .union([
              z.string(),
              z.object({ $eq: z.string() }).partial().strict(),
            ])
            .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z
        .array(
          z.object({
            fieldName: z
              .enum(['title', '_createdDate', '_updatedDate', 'sizeInBytes'])
              .optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .catchall(z.any())
    .describe(
      'Query parameters to filter, sort, and page items. All filters support equality only. Each query must include a `categoryId` filter. Supported sort fields: `createdDate`, `updatedDate`, `title`, `sizeInBytes`.'
    ),
});
export const QueryItemsResponse = z.object({
  items: z
    .array(
      z.object({
        _id: z.string().describe('Item ID.').max(100).optional(),
        mediaType: z
          .enum([
            'MIXED',
            'IMAGE',
            'VIDEO',
            'AUDIO',
            'DOCUMENT',
            'VECTOR',
            'ARCHIVE',
            'MODEL3D',
          ])
          .describe('Media type of the item.')
          .optional(),
        sizeInBytes: z
          .string()
          .describe('Size of the uploaded file in bytes.')
          .max(1000)
          .optional()
          .nullable(),
        title: z
          .string()
          .describe(
            'Title of the item. Used for searching and displaying the item.'
          )
          .max(1000)
          .optional()
          .nullable(),
        assets: z
          .intersection(
            z.object({}),
            z.xor([
              z.object({
                image: z.never().optional(),
                video: z.never().optional(),
                vector: z.never().optional(),
                audio: z.never().optional(),
                document: z.never().optional(),
                archive: z.never().optional(),
                model3d: z.never().optional(),
              }),
              z.object({
                video: z.never().optional(),
                vector: z.never().optional(),
                audio: z.never().optional(),
                document: z.never().optional(),
                archive: z.never().optional(),
                model3d: z.never().optional(),
                image: z.string().describe('Image assets and metadata.'),
              }),
              z.object({
                image: z.never().optional(),
                vector: z.never().optional(),
                audio: z.never().optional(),
                document: z.never().optional(),
                archive: z.never().optional(),
                model3d: z.never().optional(),
                video: z.string().describe('Video assets and metadata.'),
              }),
              z.object({
                image: z.never().optional(),
                video: z.never().optional(),
                audio: z.never().optional(),
                document: z.never().optional(),
                archive: z.never().optional(),
                model3d: z.never().optional(),
                vector: z
                  .string()
                  .describe('Vector image assets and metadata.'),
              }),
              z.object({
                image: z.never().optional(),
                video: z.never().optional(),
                vector: z.never().optional(),
                document: z.never().optional(),
                archive: z.never().optional(),
                model3d: z.never().optional(),
                audio: z.string().describe('Audio assets and metadata.'),
              }),
              z.object({
                image: z.never().optional(),
                video: z.never().optional(),
                vector: z.never().optional(),
                audio: z.never().optional(),
                archive: z.never().optional(),
                model3d: z.never().optional(),
                document: z.string().describe('Document assets and metadata.'),
              }),
              z.object({
                image: z.never().optional(),
                video: z.never().optional(),
                vector: z.never().optional(),
                audio: z.never().optional(),
                document: z.never().optional(),
                model3d: z.never().optional(),
                archive: z
                  .object({
                    _id: z.string().describe('WixMedia ID.').optional(),
                    url: z.string().describe('Archive URL.').optional(),
                    urlExpirationDate: z
                      .date()
                      .describe('Archive URL expiration date (when relevant).')
                      .optional()
                      .nullable(),
                    sizeInBytes: z
                      .string()
                      .describe('Archive size in bytes.')
                      .optional()
                      .nullable(),
                    filename: z
                      .string()
                      .describe('Archive filename.')
                      .optional()
                      .nullable(),
                  })
                  .describe('Archive file metadata.'),
              }),
              z.object({
                image: z.never().optional(),
                video: z.never().optional(),
                vector: z.never().optional(),
                audio: z.never().optional(),
                document: z.never().optional(),
                archive: z.never().optional(),
                model3d: z
                  .object({
                    _id: z.string().describe('WixMedia 3D ID.').optional(),
                    url: z.string().describe('3D URL.').optional(),
                    thumbnail: z
                      .string()
                      .describe('3D thumbnail Image')
                      .optional(),
                    altText: z
                      .string()
                      .describe('3D alt text.')
                      .optional()
                      .nullable(),
                    urlExpirationDate: z
                      .date()
                      .describe('3D URL expiration date (when relevant).')
                      .optional()
                      .nullable(),
                    filename: z
                      .string()
                      .describe('3D filename.')
                      .optional()
                      .nullable(),
                    sizeInBytes: z
                      .string()
                      .describe('3D size in bytes.')
                      .optional()
                      .nullable(),
                  })
                  .describe('3D model metadata.'),
              }),
            ])
          )
          .describe(
            'Item assets including URLs for different renditions and formats.'
          )
          .optional(),
        displayTags: z.array(z.string()).max(100).optional(),
        internalTags: z.array(z.string()).max(100).optional(),
        systemTags: z.array(z.string()).max(100).optional(),
        parentCategoryIds: z.array(z.string()).max(100).optional(),
        publishStatus: z
          .enum(['UNDEFINED', 'UNPUBLISHED', 'PUBLISHED'])
          .describe(
            "Publish status of the item. When `PUBLISHED`, the item is visible in child accounts' media managers. When `UNPUBLISHED`, the item is only visible in the account dashboard."
          )
          .optional(),
        _createdDate: z
          .date()
          .describe('Date and time the item was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the item was updated.')
          .optional()
          .nullable(),
        internalId: z
          .string()
          .describe(
            'Internal ID used to reference the item within Wix media systems.'
          )
          .max(100)
          .optional()
          .nullable(),
      })
    )
    .max(200)
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe(
          'Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe('Cursor pointing to next page in the list of results.')
            .optional()
            .nullable(),
        })
        .describe(
          'Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used.'
        )
        .optional(),
    })
    .describe('Paging information for the query results.')
    .optional(),
});
export const UpdateItemRequest = z.object({
  _id: z.string().describe('Item ID.').max(100),
  item: z
    .object({
      _id: z.string().describe('Item ID.').max(100).optional(),
      mediaType: z
        .enum([
          'MIXED',
          'IMAGE',
          'VIDEO',
          'AUDIO',
          'DOCUMENT',
          'VECTOR',
          'ARCHIVE',
          'MODEL3D',
        ])
        .optional(),
      sizeInBytes: z
        .string()
        .describe('Size of the uploaded file in bytes.')
        .max(1000)
        .optional()
        .nullable(),
      title: z
        .string()
        .describe(
          'Title of the item. Used for searching and displaying the item.'
        )
        .max(1000)
        .optional()
        .nullable(),
      assets: z
        .intersection(
          z.object({}),
          z.xor([
            z.object({
              image: z.never().optional(),
              video: z.never().optional(),
              vector: z.never().optional(),
              audio: z.never().optional(),
              document: z.never().optional(),
              archive: z.never().optional(),
              model3d: z.never().optional(),
            }),
            z.object({
              video: z.never().optional(),
              vector: z.never().optional(),
              audio: z.never().optional(),
              document: z.never().optional(),
              archive: z.never().optional(),
              model3d: z.never().optional(),
              image: z.string().describe('Image assets and metadata.'),
            }),
            z.object({
              image: z.never().optional(),
              vector: z.never().optional(),
              audio: z.never().optional(),
              document: z.never().optional(),
              archive: z.never().optional(),
              model3d: z.never().optional(),
              video: z.string().describe('Video assets and metadata.'),
            }),
            z.object({
              image: z.never().optional(),
              video: z.never().optional(),
              audio: z.never().optional(),
              document: z.never().optional(),
              archive: z.never().optional(),
              model3d: z.never().optional(),
              vector: z.string().describe('Vector image assets and metadata.'),
            }),
            z.object({
              image: z.never().optional(),
              video: z.never().optional(),
              vector: z.never().optional(),
              document: z.never().optional(),
              archive: z.never().optional(),
              model3d: z.never().optional(),
              audio: z.string().describe('Audio assets and metadata.'),
            }),
            z.object({
              image: z.never().optional(),
              video: z.never().optional(),
              vector: z.never().optional(),
              audio: z.never().optional(),
              archive: z.never().optional(),
              model3d: z.never().optional(),
              document: z.string().describe('Document assets and metadata.'),
            }),
            z.object({
              image: z.never().optional(),
              video: z.never().optional(),
              vector: z.never().optional(),
              audio: z.never().optional(),
              document: z.never().optional(),
              model3d: z.never().optional(),
              archive: z
                .object({
                  _id: z.string().describe('WixMedia ID.').optional(),
                  url: z.string().describe('Archive URL.').optional(),
                  urlExpirationDate: z
                    .date()
                    .describe('Archive URL expiration date (when relevant).')
                    .optional()
                    .nullable(),
                  sizeInBytes: z
                    .string()
                    .describe('Archive size in bytes.')
                    .optional()
                    .nullable(),
                  filename: z
                    .string()
                    .describe('Archive filename.')
                    .optional()
                    .nullable(),
                })
                .describe('Archive file metadata.'),
            }),
            z.object({
              image: z.never().optional(),
              video: z.never().optional(),
              vector: z.never().optional(),
              audio: z.never().optional(),
              document: z.never().optional(),
              archive: z.never().optional(),
              model3d: z
                .object({
                  _id: z.string().describe('WixMedia 3D ID.').optional(),
                  url: z.string().describe('3D URL.').optional(),
                  thumbnail: z
                    .string()
                    .describe('3D thumbnail Image')
                    .optional(),
                  altText: z
                    .string()
                    .describe('3D alt text.')
                    .optional()
                    .nullable(),
                  urlExpirationDate: z
                    .date()
                    .describe('3D URL expiration date (when relevant).')
                    .optional()
                    .nullable(),
                  filename: z
                    .string()
                    .describe('3D filename.')
                    .optional()
                    .nullable(),
                  sizeInBytes: z
                    .string()
                    .describe('3D size in bytes.')
                    .optional()
                    .nullable(),
                })
                .describe('3D model metadata.'),
            }),
          ])
        )
        .describe(
          'Item assets including URLs for different renditions and formats.'
        )
        .optional(),
      displayTags: z.array(z.string()).max(100).optional(),
      internalTags: z.array(z.string()).max(100).optional(),
      systemTags: z.array(z.string()).max(100).optional(),
      parentCategoryIds: z.array(z.string()).max(100).optional(),
      publishStatus: z
        .enum(['UNDEFINED', 'UNPUBLISHED', 'PUBLISHED'])
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the item was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the item was updated.')
        .optional()
        .nullable(),
      internalId: z
        .string()
        .describe(
          'Internal ID used to reference the item within Wix media systems.'
        )
        .max(100)
        .optional()
        .nullable(),
    })
    .describe('Item to update.'),
});
export const UpdateItemResponse = z.object({
  _id: z.string().describe('Item ID.').max(100).optional(),
  mediaType: z
    .enum([
      'MIXED',
      'IMAGE',
      'VIDEO',
      'AUDIO',
      'DOCUMENT',
      'VECTOR',
      'ARCHIVE',
      'MODEL3D',
    ])
    .describe('Media type of the item.')
    .optional(),
  sizeInBytes: z
    .string()
    .describe('Size of the uploaded file in bytes.')
    .max(1000)
    .optional()
    .nullable(),
  title: z
    .string()
    .describe('Title of the item. Used for searching and displaying the item.')
    .max(1000)
    .optional()
    .nullable(),
  assets: z
    .intersection(
      z.object({}),
      z.xor([
        z.object({
          image: z.never().optional(),
          video: z.never().optional(),
          vector: z.never().optional(),
          audio: z.never().optional(),
          document: z.never().optional(),
          archive: z.never().optional(),
          model3d: z.never().optional(),
        }),
        z.object({
          video: z.never().optional(),
          vector: z.never().optional(),
          audio: z.never().optional(),
          document: z.never().optional(),
          archive: z.never().optional(),
          model3d: z.never().optional(),
          image: z.string().describe('Image assets and metadata.'),
        }),
        z.object({
          image: z.never().optional(),
          vector: z.never().optional(),
          audio: z.never().optional(),
          document: z.never().optional(),
          archive: z.never().optional(),
          model3d: z.never().optional(),
          video: z.string().describe('Video assets and metadata.'),
        }),
        z.object({
          image: z.never().optional(),
          video: z.never().optional(),
          audio: z.never().optional(),
          document: z.never().optional(),
          archive: z.never().optional(),
          model3d: z.never().optional(),
          vector: z.string().describe('Vector image assets and metadata.'),
        }),
        z.object({
          image: z.never().optional(),
          video: z.never().optional(),
          vector: z.never().optional(),
          document: z.never().optional(),
          archive: z.never().optional(),
          model3d: z.never().optional(),
          audio: z.string().describe('Audio assets and metadata.'),
        }),
        z.object({
          image: z.never().optional(),
          video: z.never().optional(),
          vector: z.never().optional(),
          audio: z.never().optional(),
          archive: z.never().optional(),
          model3d: z.never().optional(),
          document: z.string().describe('Document assets and metadata.'),
        }),
        z.object({
          image: z.never().optional(),
          video: z.never().optional(),
          vector: z.never().optional(),
          audio: z.never().optional(),
          document: z.never().optional(),
          model3d: z.never().optional(),
          archive: z
            .object({
              _id: z.string().describe('WixMedia ID.').optional(),
              url: z.string().describe('Archive URL.').optional(),
              urlExpirationDate: z
                .date()
                .describe('Archive URL expiration date (when relevant).')
                .optional()
                .nullable(),
              sizeInBytes: z
                .string()
                .describe('Archive size in bytes.')
                .optional()
                .nullable(),
              filename: z
                .string()
                .describe('Archive filename.')
                .optional()
                .nullable(),
            })
            .describe('Archive file metadata.'),
        }),
        z.object({
          image: z.never().optional(),
          video: z.never().optional(),
          vector: z.never().optional(),
          audio: z.never().optional(),
          document: z.never().optional(),
          archive: z.never().optional(),
          model3d: z
            .object({
              _id: z.string().describe('WixMedia 3D ID.').optional(),
              url: z.string().describe('3D URL.').optional(),
              thumbnail: z.string().describe('3D thumbnail Image').optional(),
              altText: z
                .string()
                .describe('3D alt text.')
                .optional()
                .nullable(),
              urlExpirationDate: z
                .date()
                .describe('3D URL expiration date (when relevant).')
                .optional()
                .nullable(),
              filename: z
                .string()
                .describe('3D filename.')
                .optional()
                .nullable(),
              sizeInBytes: z
                .string()
                .describe('3D size in bytes.')
                .optional()
                .nullable(),
            })
            .describe('3D model metadata.'),
        }),
      ])
    )
    .describe(
      'Item assets including URLs for different renditions and formats.'
    )
    .optional(),
  displayTags: z.array(z.string()).max(100).optional(),
  internalTags: z.array(z.string()).max(100).optional(),
  systemTags: z.array(z.string()).max(100).optional(),
  parentCategoryIds: z.array(z.string()).max(100).optional(),
  publishStatus: z
    .enum(['UNDEFINED', 'UNPUBLISHED', 'PUBLISHED'])
    .describe(
      "Publish status of the item. When `PUBLISHED`, the item is visible in child accounts' media managers. When `UNPUBLISHED`, the item is only visible in the account dashboard."
    )
    .optional(),
  _createdDate: z
    .date()
    .describe('Date and time the item was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the item was updated.')
    .optional()
    .nullable(),
  internalId: z
    .string()
    .describe(
      'Internal ID used to reference the item within Wix media systems.'
    )
    .max(100)
    .optional()
    .nullable(),
});
export const BulkUpdateItemRequest = z.object({
  updateItemRequests: z
    .array(
      z.object({
        item: z
          .object({
            _id: z.string().describe('Item ID.').max(100),
            mediaType: z
              .enum([
                'MIXED',
                'IMAGE',
                'VIDEO',
                'AUDIO',
                'DOCUMENT',
                'VECTOR',
                'ARCHIVE',
                'MODEL3D',
              ])
              .optional(),
            sizeInBytes: z
              .string()
              .describe('Size of the uploaded file in bytes.')
              .max(1000)
              .optional()
              .nullable(),
            title: z
              .string()
              .describe(
                'Title of the item. Used for searching and displaying the item.'
              )
              .max(1000)
              .optional()
              .nullable(),
            assets: z
              .intersection(
                z.object({}),
                z.xor([
                  z.object({
                    image: z.never().optional(),
                    video: z.never().optional(),
                    vector: z.never().optional(),
                    audio: z.never().optional(),
                    document: z.never().optional(),
                    archive: z.never().optional(),
                    model3d: z.never().optional(),
                  }),
                  z.object({
                    video: z.never().optional(),
                    vector: z.never().optional(),
                    audio: z.never().optional(),
                    document: z.never().optional(),
                    archive: z.never().optional(),
                    model3d: z.never().optional(),
                    image: z.string().describe('Image assets and metadata.'),
                  }),
                  z.object({
                    image: z.never().optional(),
                    vector: z.never().optional(),
                    audio: z.never().optional(),
                    document: z.never().optional(),
                    archive: z.never().optional(),
                    model3d: z.never().optional(),
                    video: z.string().describe('Video assets and metadata.'),
                  }),
                  z.object({
                    image: z.never().optional(),
                    video: z.never().optional(),
                    audio: z.never().optional(),
                    document: z.never().optional(),
                    archive: z.never().optional(),
                    model3d: z.never().optional(),
                    vector: z
                      .string()
                      .describe('Vector image assets and metadata.'),
                  }),
                  z.object({
                    image: z.never().optional(),
                    video: z.never().optional(),
                    vector: z.never().optional(),
                    document: z.never().optional(),
                    archive: z.never().optional(),
                    model3d: z.never().optional(),
                    audio: z.string().describe('Audio assets and metadata.'),
                  }),
                  z.object({
                    image: z.never().optional(),
                    video: z.never().optional(),
                    vector: z.never().optional(),
                    audio: z.never().optional(),
                    archive: z.never().optional(),
                    model3d: z.never().optional(),
                    document: z
                      .string()
                      .describe('Document assets and metadata.'),
                  }),
                  z.object({
                    image: z.never().optional(),
                    video: z.never().optional(),
                    vector: z.never().optional(),
                    audio: z.never().optional(),
                    document: z.never().optional(),
                    model3d: z.never().optional(),
                    archive: z
                      .object({
                        _id: z.string().describe('WixMedia ID.').optional(),
                        url: z.string().describe('Archive URL.').optional(),
                        urlExpirationDate: z
                          .date()
                          .describe(
                            'Archive URL expiration date (when relevant).'
                          )
                          .optional()
                          .nullable(),
                        sizeInBytes: z
                          .string()
                          .describe('Archive size in bytes.')
                          .optional()
                          .nullable(),
                        filename: z
                          .string()
                          .describe('Archive filename.')
                          .optional()
                          .nullable(),
                      })
                      .describe('Archive file metadata.'),
                  }),
                  z.object({
                    image: z.never().optional(),
                    video: z.never().optional(),
                    vector: z.never().optional(),
                    audio: z.never().optional(),
                    document: z.never().optional(),
                    archive: z.never().optional(),
                    model3d: z
                      .object({
                        _id: z.string().describe('WixMedia 3D ID.').optional(),
                        url: z.string().describe('3D URL.').optional(),
                        thumbnail: z
                          .string()
                          .describe('3D thumbnail Image')
                          .optional(),
                        altText: z
                          .string()
                          .describe('3D alt text.')
                          .optional()
                          .nullable(),
                        urlExpirationDate: z
                          .date()
                          .describe('3D URL expiration date (when relevant).')
                          .optional()
                          .nullable(),
                        filename: z
                          .string()
                          .describe('3D filename.')
                          .optional()
                          .nullable(),
                        sizeInBytes: z
                          .string()
                          .describe('3D size in bytes.')
                          .optional()
                          .nullable(),
                      })
                      .describe('3D model metadata.'),
                  }),
                ])
              )
              .describe(
                'Item assets including URLs for different renditions and formats.'
              )
              .optional(),
            displayTags: z.array(z.string()).max(100).optional(),
            internalTags: z.array(z.string()).max(100).optional(),
            systemTags: z.array(z.string()).max(100).optional(),
            parentCategoryIds: z.array(z.string()).max(100).optional(),
            publishStatus: z
              .enum(['UNDEFINED', 'UNPUBLISHED', 'PUBLISHED'])
              .optional(),
            _createdDate: z
              .date()
              .describe('Date and time the item was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the item was updated.')
              .optional()
              .nullable(),
            internalId: z
              .string()
              .describe(
                'Internal ID used to reference the item within Wix media systems.'
              )
              .max(100)
              .optional()
              .nullable(),
          })
          .describe('Item to update.')
          .optional(),
      })
    )
    .max(20)
    .min(1),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe('Whether to return the updated items in the response.')
        .optional(),
    })
    .optional(),
});
export const BulkUpdateItemResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata about the update operation result.')
          .optional(),
        item: z
          .object({
            _id: z.string().describe('Item ID.').max(100).optional(),
            mediaType: z
              .enum([
                'MIXED',
                'IMAGE',
                'VIDEO',
                'AUDIO',
                'DOCUMENT',
                'VECTOR',
                'ARCHIVE',
                'MODEL3D',
              ])
              .describe('Media type of the item.')
              .optional(),
            sizeInBytes: z
              .string()
              .describe('Size of the uploaded file in bytes.')
              .max(1000)
              .optional()
              .nullable(),
            title: z
              .string()
              .describe(
                'Title of the item. Used for searching and displaying the item.'
              )
              .max(1000)
              .optional()
              .nullable(),
            assets: z
              .intersection(
                z.object({}),
                z.xor([
                  z.object({
                    image: z.never().optional(),
                    video: z.never().optional(),
                    vector: z.never().optional(),
                    audio: z.never().optional(),
                    document: z.never().optional(),
                    archive: z.never().optional(),
                    model3d: z.never().optional(),
                  }),
                  z.object({
                    video: z.never().optional(),
                    vector: z.never().optional(),
                    audio: z.never().optional(),
                    document: z.never().optional(),
                    archive: z.never().optional(),
                    model3d: z.never().optional(),
                    image: z.string().describe('Image assets and metadata.'),
                  }),
                  z.object({
                    image: z.never().optional(),
                    vector: z.never().optional(),
                    audio: z.never().optional(),
                    document: z.never().optional(),
                    archive: z.never().optional(),
                    model3d: z.never().optional(),
                    video: z.string().describe('Video assets and metadata.'),
                  }),
                  z.object({
                    image: z.never().optional(),
                    video: z.never().optional(),
                    audio: z.never().optional(),
                    document: z.never().optional(),
                    archive: z.never().optional(),
                    model3d: z.never().optional(),
                    vector: z
                      .string()
                      .describe('Vector image assets and metadata.'),
                  }),
                  z.object({
                    image: z.never().optional(),
                    video: z.never().optional(),
                    vector: z.never().optional(),
                    document: z.never().optional(),
                    archive: z.never().optional(),
                    model3d: z.never().optional(),
                    audio: z.string().describe('Audio assets and metadata.'),
                  }),
                  z.object({
                    image: z.never().optional(),
                    video: z.never().optional(),
                    vector: z.never().optional(),
                    audio: z.never().optional(),
                    archive: z.never().optional(),
                    model3d: z.never().optional(),
                    document: z
                      .string()
                      .describe('Document assets and metadata.'),
                  }),
                  z.object({
                    image: z.never().optional(),
                    video: z.never().optional(),
                    vector: z.never().optional(),
                    audio: z.never().optional(),
                    document: z.never().optional(),
                    model3d: z.never().optional(),
                    archive: z
                      .object({
                        _id: z.string().describe('WixMedia ID.').optional(),
                        url: z.string().describe('Archive URL.').optional(),
                        urlExpirationDate: z
                          .date()
                          .describe(
                            'Archive URL expiration date (when relevant).'
                          )
                          .optional()
                          .nullable(),
                        sizeInBytes: z
                          .string()
                          .describe('Archive size in bytes.')
                          .optional()
                          .nullable(),
                        filename: z
                          .string()
                          .describe('Archive filename.')
                          .optional()
                          .nullable(),
                      })
                      .describe('Archive file metadata.'),
                  }),
                  z.object({
                    image: z.never().optional(),
                    video: z.never().optional(),
                    vector: z.never().optional(),
                    audio: z.never().optional(),
                    document: z.never().optional(),
                    archive: z.never().optional(),
                    model3d: z
                      .object({
                        _id: z.string().describe('WixMedia 3D ID.').optional(),
                        url: z.string().describe('3D URL.').optional(),
                        thumbnail: z
                          .string()
                          .describe('3D thumbnail Image')
                          .optional(),
                        altText: z
                          .string()
                          .describe('3D alt text.')
                          .optional()
                          .nullable(),
                        urlExpirationDate: z
                          .date()
                          .describe('3D URL expiration date (when relevant).')
                          .optional()
                          .nullable(),
                        filename: z
                          .string()
                          .describe('3D filename.')
                          .optional()
                          .nullable(),
                        sizeInBytes: z
                          .string()
                          .describe('3D size in bytes.')
                          .optional()
                          .nullable(),
                      })
                      .describe('3D model metadata.'),
                  }),
                ])
              )
              .describe(
                'Item assets including URLs for different renditions and formats.'
              )
              .optional(),
            displayTags: z.array(z.string()).max(100).optional(),
            internalTags: z.array(z.string()).max(100).optional(),
            systemTags: z.array(z.string()).max(100).optional(),
            parentCategoryIds: z.array(z.string()).max(100).optional(),
            publishStatus: z
              .enum(['UNDEFINED', 'UNPUBLISHED', 'PUBLISHED'])
              .describe(
                "Publish status of the item. When `PUBLISHED`, the item is visible in child accounts' media managers. When `UNPUBLISHED`, the item is only visible in the account dashboard."
              )
              .optional(),
            _createdDate: z
              .date()
              .describe('Date and time the item was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the item was updated.')
              .optional()
              .nullable(),
            internalId: z
              .string()
              .describe(
                'Internal ID used to reference the item within Wix media systems.'
              )
              .max(100)
              .optional()
              .nullable(),
          })
          .describe(
            'Updated item. Returned only if the operation was successful and `returnEntity` was set to `true`.'
          )
          .optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Metadata about the bulk operation.')
    .optional(),
});
export const GetItemRequest = z.object({
  itemId: z.string().describe('Item ID.').max(100),
});
export const GetItemResponse = z.object({
  _id: z.string().describe('Item ID.').max(100).optional(),
  mediaType: z
    .enum([
      'MIXED',
      'IMAGE',
      'VIDEO',
      'AUDIO',
      'DOCUMENT',
      'VECTOR',
      'ARCHIVE',
      'MODEL3D',
    ])
    .describe('Media type of the item.')
    .optional(),
  sizeInBytes: z
    .string()
    .describe('Size of the uploaded file in bytes.')
    .max(1000)
    .optional()
    .nullable(),
  title: z
    .string()
    .describe('Title of the item. Used for searching and displaying the item.')
    .max(1000)
    .optional()
    .nullable(),
  assets: z
    .intersection(
      z.object({}),
      z.xor([
        z.object({
          image: z.never().optional(),
          video: z.never().optional(),
          vector: z.never().optional(),
          audio: z.never().optional(),
          document: z.never().optional(),
          archive: z.never().optional(),
          model3d: z.never().optional(),
        }),
        z.object({
          video: z.never().optional(),
          vector: z.never().optional(),
          audio: z.never().optional(),
          document: z.never().optional(),
          archive: z.never().optional(),
          model3d: z.never().optional(),
          image: z.string().describe('Image assets and metadata.'),
        }),
        z.object({
          image: z.never().optional(),
          vector: z.never().optional(),
          audio: z.never().optional(),
          document: z.never().optional(),
          archive: z.never().optional(),
          model3d: z.never().optional(),
          video: z.string().describe('Video assets and metadata.'),
        }),
        z.object({
          image: z.never().optional(),
          video: z.never().optional(),
          audio: z.never().optional(),
          document: z.never().optional(),
          archive: z.never().optional(),
          model3d: z.never().optional(),
          vector: z.string().describe('Vector image assets and metadata.'),
        }),
        z.object({
          image: z.never().optional(),
          video: z.never().optional(),
          vector: z.never().optional(),
          document: z.never().optional(),
          archive: z.never().optional(),
          model3d: z.never().optional(),
          audio: z.string().describe('Audio assets and metadata.'),
        }),
        z.object({
          image: z.never().optional(),
          video: z.never().optional(),
          vector: z.never().optional(),
          audio: z.never().optional(),
          archive: z.never().optional(),
          model3d: z.never().optional(),
          document: z.string().describe('Document assets and metadata.'),
        }),
        z.object({
          image: z.never().optional(),
          video: z.never().optional(),
          vector: z.never().optional(),
          audio: z.never().optional(),
          document: z.never().optional(),
          model3d: z.never().optional(),
          archive: z
            .object({
              _id: z.string().describe('WixMedia ID.').optional(),
              url: z.string().describe('Archive URL.').optional(),
              urlExpirationDate: z
                .date()
                .describe('Archive URL expiration date (when relevant).')
                .optional()
                .nullable(),
              sizeInBytes: z
                .string()
                .describe('Archive size in bytes.')
                .optional()
                .nullable(),
              filename: z
                .string()
                .describe('Archive filename.')
                .optional()
                .nullable(),
            })
            .describe('Archive file metadata.'),
        }),
        z.object({
          image: z.never().optional(),
          video: z.never().optional(),
          vector: z.never().optional(),
          audio: z.never().optional(),
          document: z.never().optional(),
          archive: z.never().optional(),
          model3d: z
            .object({
              _id: z.string().describe('WixMedia 3D ID.').optional(),
              url: z.string().describe('3D URL.').optional(),
              thumbnail: z.string().describe('3D thumbnail Image').optional(),
              altText: z
                .string()
                .describe('3D alt text.')
                .optional()
                .nullable(),
              urlExpirationDate: z
                .date()
                .describe('3D URL expiration date (when relevant).')
                .optional()
                .nullable(),
              filename: z
                .string()
                .describe('3D filename.')
                .optional()
                .nullable(),
              sizeInBytes: z
                .string()
                .describe('3D size in bytes.')
                .optional()
                .nullable(),
            })
            .describe('3D model metadata.'),
        }),
      ])
    )
    .describe(
      'Item assets including URLs for different renditions and formats.'
    )
    .optional(),
  displayTags: z.array(z.string()).max(100).optional(),
  internalTags: z.array(z.string()).max(100).optional(),
  systemTags: z.array(z.string()).max(100).optional(),
  parentCategoryIds: z.array(z.string()).max(100).optional(),
  publishStatus: z
    .enum(['UNDEFINED', 'UNPUBLISHED', 'PUBLISHED'])
    .describe(
      "Publish status of the item. When `PUBLISHED`, the item is visible in child accounts' media managers. When `UNPUBLISHED`, the item is only visible in the account dashboard."
    )
    .optional(),
  _createdDate: z
    .date()
    .describe('Date and time the item was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the item was updated.')
    .optional()
    .nullable(),
  internalId: z
    .string()
    .describe(
      'Internal ID used to reference the item within Wix media systems.'
    )
    .max(100)
    .optional()
    .nullable(),
});
export const LinkItemToCategoriesRequest = z.object({
  itemId: z.string().describe('Item ID.').max(100),
  options: z
    .object({ categoryIds: z.array(z.string()).max(20).optional() })
    .optional(),
});
export const LinkItemToCategoriesResponse = z.object({
  linkedCategoryIds: z.array(z.string()).max(20).optional(),
});
export const UnlinkItemFromCategoriesRequest = z.object({
  itemId: z.string().describe('Item ID.').max(100),
  options: z
    .object({ categoryIds: z.array(z.string()).max(20).optional() })
    .optional(),
});
export const UnlinkItemFromCategoriesResponse = z.object({
  unlinkedCategoryIds: z.array(z.string()).max(20).optional(),
});
export const OverwriteItemCategoriesRequest = z.object({
  itemId: z.string().describe('Item ID.').max(100),
  options: z
    .object({ categoryIds: z.array(z.string()).max(20).optional() })
    .optional(),
});
export const OverwriteItemCategoriesResponse = z.object({
  linkedCategoryIds: z.array(z.string()).max(20).optional(),
  unlinkedCategoryIds: z.array(z.string()).max(20).optional(),
});
